<template>
  <div class="job-positions-catalog">
    <div
      class="job-positions-catalog__filter"
      v-if="!preview"
      data-aos="fade-down"
      data-aos-delay="150"
    >
      <DropDownSelect
        title="Department"
        :options="departmentOptions"
        icon="fa-pen"
        v-model="department"
        multiple
      />
      <DropDownSelect
        title="Location"
        :options="locationOptions"
        icon="fa-location-dot"
        v-model="location"
        multiple
      />
      <DropDownSelect
        title="Employment"
        :options="employmentTypeOptions"
        icon="fa-clock"
        v-model="employmentType"
        multiple
      />
    </div>
    <div
      class="job-positions-catalog__positions"
      v-if="visibleJobs && visibleJobs.length > 0"
      data-aos="fade"
      data-aos-delay="500"
    >
      <JobPositionCard
        class="job-positions-catalog__position-card"
        v-for="(position, i) in visibleJobs"
        :key="position.name"
        :name="position.name"
        :type="position.employmentType"
        :location="position.location"
        :department="position.department"
        :id="position.id"
      />
    </div>
    <p class="job-positions-catalog-placeholder" v-else>
      <i class="job-positions-catalog-placeholder__icon fa-solid fa-ban" />
      <span class="job-positions-catalog-placeholder__text">
        Sorry, there are no open positions at the moment =(
      </span>
    </p>
    <Button size="lg" href="/careers" v-if="preview"> More Jobs </Button>
  </div>
</template>

<script setup lang="ts">
  import type { DropDownSelectOption } from "@website/components/selects/DropDownSelect.vue";
import Button from "../buttons/Button.vue";

  const props = defineProps<{
    preview?: boolean;
    jobs: {
      name: string;
      employmentType?: string;
      location?: string;
      department?: string;
      id: number;
    }[];
  }>();
  
  const departmentOptions = computed(() =>
    props.jobs.reduce<DropDownSelectOption[]>((acc, job) => {
      if (
        job.department &&
        !acc.some((accPosition) => accPosition.value === job.department)
      )
        acc.push({
          label: job.department,
          value: job.department,
        });

      return acc;
    }, []) ?? []
  );
  const locationOptions = computed(() =>
    props.jobs.reduce<DropDownSelectOption[]>((acc, job) => {
      if (
        job.location &&
        !acc.some((accPosition) => accPosition.value === job.location)
      )
        acc.push({
          label: job.location,
          value: job.location,
        });

      return acc;
    }, []) ?? []
  );
  const employmentTypeOptions = computed(() =>
    props.jobs.reduce<DropDownSelectOption[]>((acc, job) => {
      if (
        job.employmentType &&
        !acc.some(
          (accPosition) => accPosition.value === job.employmentType
        )
      )
        acc.push({
          label: job.employmentType,
          value: job.employmentType,
        });

      return acc;
    }, []) ?? []
  );

  const department = ref<string[] | null>(null);
  const location = ref<string[] | null>(null);
  const employmentType = ref<string[] | null>(null);

  const visibleJobs = computed(() =>
    props.jobs
      ?.filter(
        (job) =>
          (!department.value ||
            !job.department ||
            department.value.includes(job.department)) &&
          (!location.value ||
            !job.location ||
            location.value.includes(job.location)) &&
          (!employmentType.value ||
            !job.employmentType ||
            employmentType.value.includes(job.employmentType))
      ) ?? []
      .slice(0, 4)
  );
</script>

<style lang="scss">
  .job-positions-catalog {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 32px;

    margin-bottom: 32px;

    max-width: 100%;

    &__filter {
      display: flex;
      justify-content: center;
      gap: 16px;
      position: relative;
      z-index: 100;

      @media (max-width: $lg) {
        width: 100%;
        flex-direction: column;
      }
    }

    &__positions {
      display: flex;
      flex-direction: column;
      gap: 16px;

      max-width: 100%;
      width: 1080px;
    }
  }

  .job-positions-catalog-placeholder {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 16px;

    padding: 64px 0;

    text-align: center;
    color: color-mix(in srgb, #212529, transparent 50%);

    &__icon {
      font-size: 64px;
    }

    &__text {
      font-family: var(--font-family-main);
      font-size: 36px;
      line-height: 1;
      font-weight: 400;
    }
  }
</style>
